'use client'

import * as React from 'react'
import { Check, ChevronsUpDown, Trash2, X } from 'lucide-react'
import { Button } from '../base/button'
import { Badge } from '../base/badge'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '../base/command'
import { Popover, PopoverContent, PopoverTrigger } from '../base/popover'
import { cn } from '@/lib/utils'
import { SearchableDropdownProps } from '@frontend/components/enhanced/searchable-dropdown'

export function MultiSelectSearchableDropdown({
  name,
  items = [],
  options = [],
  value,
  onSelect,
  onChange,
  label,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  className,
  disabledValue,
  inputVariant,
  error = false,
  errorMessage,
  initialDisplayCount = 20,
  searchThreshold = 0,
  enablePagination = true,
  clearButton = true,
  maxBadgeDisplay = 3,
  keepConsistentWidth = false,
  disabledValues = []
}: SearchableDropdownProps) {
  const [open, setOpen] = React.useState(false)
  const [searchValue, setSearchValue] = React.useState('')
  const [displayCount, setDisplayCount] = React.useState(initialDisplayCount)

  // Use items if provided, otherwise fall back to options
  const optionsList = items.length > 0 ? items : options

  // Filter options based on search
  const filteredOptions = React.useMemo(() => {
    if (!searchValue || searchValue.length < searchThreshold) {
      return optionsList
    }

    return optionsList.filter((option) =>
      option.name.toLowerCase().includes(searchValue.toLowerCase())
    )
  }, [optionsList, searchValue, searchThreshold])

  // Apply display limit for performance
  const displayedOptions = React.useMemo(() => {
    if (searchValue && searchValue.length >= searchThreshold) {
      // When searching, show all results (already filtered)
      return filteredOptions
    }
    // When not searching, limit initial display
    return filteredOptions.slice(0, displayCount)
  }, [filteredOptions, displayCount, searchValue, searchThreshold])

  // Use onSelect if provided, otherwise fall back to onChange
  const handleChange = onSelect || onChange

  const handleSelect = (optionValue: string) => {
    if (handleChange) {
      const currentValues = Array.isArray(value) ? value : []
      const newValues = currentValues.includes(optionValue)
        ? currentValues.filter((v) => v !== optionValue)
        : [...currentValues, optionValue]
      handleChange(newValues)
    }
    setSearchValue('') // Reset search when selection is made
    setDisplayCount(initialDisplayCount) // Reset display count
  }

  const removeItem = (itemToRemove: string) => {
    if (handleChange && Array.isArray(value)) {
      handleChange(value.filter((v) => v !== itemToRemove))
    }
  }

  const handleLoadMore = () => {
    setDisplayCount((prev) =>
      Math.min(prev + initialDisplayCount, filteredOptions.length)
    )
  }

  const handleSearchChange = (search: string) => {
    setSearchValue(search)
    setDisplayCount(initialDisplayCount) // Reset display count when searching
  }

  const clearSelection = () => {
    if (handleChange) {
      handleChange([])
    }
  }

  const selectedOption = optionsList.find(
    (option) => option.value?.toString() === value?.toString()
  )

  const hasMore =
    !searchValue && displayedOptions.length < filteredOptions.length

  return (
    <div className={cn('space-y-1', className)}>
      {label && (
        <label htmlFor={name} className='text-sm font-medium'>
          {label}
        </label>
      )}
      <div className='flex gap-1'>
        <Popover open={open} onOpenChange={setOpen} modal={true}>
          <PopoverTrigger asChild>
            <Button
              variant='outline'
              role='combobox'
              aria-expanded={open}
              className={cn(
                'border-input min-w-0 flex-1 justify-between px-2',
                error && 'border-red-500'
              )}
            >
              <div className='flex min-w-0 flex-1 items-center gap-1'>
                {Array.isArray(value) && value.length > 0 ? (
                  <div className='flex min-w-0 flex-1 flex-wrap items-center gap-1'>
                    {value.slice(0, maxBadgeDisplay).map((val) => {
                      const option = optionsList.find(
                        (o) => o.value?.toString() === val
                      )
                      return (
                        <Badge
                          key={val}
                          variant='secondary'
                          className='h-4 flex-shrink-0 px-1 text-xs'
                        >
                          <span className='max-w-16 truncate'>
                            {option?.name}
                          </span>
                          <button
                            className='ml-1 rounded-full hover:bg-gray-300'
                            onClick={(e) => {
                              e.stopPropagation()
                              removeItem(val)
                            }}
                          >
                            <X className='h-2 w-2' />
                          </button>
                        </Badge>
                      )
                    })}
                    {value.length > maxBadgeDisplay && (
                      <span className='text-muted-foreground flex-shrink-0 text-xs'>
                        +{value.length - maxBadgeDisplay} more
                      </span>
                    )}
                  </div>
                ) : !value || (Array.isArray(value) && value.length === 0) ? (
                  <span className='text-muted-foreground truncate text-xs'>
                    {placeholder}
                  </span>
                ) : (
                  <div className='flex min-w-0 flex-1 items-center gap-2 overflow-hidden'>
                    {selectedOption?.logo && (
                      <div className='flex-shrink-0'>
                        {typeof selectedOption.logo === 'string' ? (
                          <img
                            src={selectedOption.logo}
                            alt=''
                            className='h-4 w-4 object-contain'
                          />
                        ) : (
                          selectedOption.logo
                        )}
                      </div>
                    )}
                    <span className='truncate'>{selectedOption?.name}</span>
                  </div>
                )}
              </div>
              <ChevronsUpDown className='ml-1 h-3 w-3 shrink-0 opacity-50' />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className='p-0'
            align='start'
            style={{
              zIndex: 9999,
              width: keepConsistentWidth
                ? 'var(--radix-popover-trigger-width)'
                : '100%'
            }}
          >
            <Command shouldFilter={false}>
              <CommandInput
                placeholder={searchPlaceholder}
                className='h-8'
                value={searchValue}
                onValueChange={handleSearchChange}
              />
              <CommandList className='max-h-60 overflow-y-auto'>
                <CommandEmpty>No options found.</CommandEmpty>
                <CommandGroup>
                  {displayedOptions.map((option, index) => (
                    <CommandItem
                      key={option.id || option.value || index}
                      value={`${option.name}|||${option.value}`}
                      onSelect={() => handleSelect(option.value?.toString())}
                      className='text-xs'
                      disabled={
                        option.value === disabledValue ||
                        disabledValues?.includes(option.value)
                      }
                    >
                      <Check
                        className={cn(
                          'mr-2 h-3 w-3',
                          Array.isArray(value) &&
                            value.includes(option.value?.toString())
                            ? 'opacity-100'
                            : 'opacity-0'
                        )}
                      />
                      <div className='flex flex-1 items-center gap-2'>
                        {option.logo && (
                          <div className='flex-shrink-0'>
                            {typeof option.logo === 'string' ? (
                              <img
                                src={option.logo}
                                alt=''
                                className='h-4 w-4 object-contain'
                              />
                            ) : (
                              option.logo
                            )}
                          </div>
                        )}
                        <span>{option.name}</span>
                      </div>
                    </CommandItem>
                  ))}
                  {hasMore && enablePagination && (
                    <CommandItem
                      key='load-more'
                      onSelect={handleLoadMore}
                      className='text-muted-foreground mt-1 border-t pt-1 text-center text-xs'
                    >
                      Load more... (
                      {filteredOptions.length - displayedOptions.length}{' '}
                      remaining)
                    </CommandItem>
                  )}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Clear Button */}
        {clearButton &&
          ((Array.isArray(value) && value.length > 0) ||
            (!Array.isArray(value) && value)) && (
            <Button
              variant='outline'
              size='sm'
              onClick={clearSelection}
              className='h-[36px] w-8 flex-shrink-0 bg-transparent p-0 hover:border-red-200 hover:bg-red-50'
              title='Clear selection'
            >
              <Trash2 className='h-3 w-3 text-red-500' />
            </Button>
          )}
      </div>
      {error && errorMessage && (
        <p className='text-xs text-red-500'>{errorMessage}</p>
      )}
    </div>
  )
}
